import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  MenuList,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { STRONG_DROPDOWN_PAPER_SX } from "components/common/dropdownPaper";
import { endOfDay, startOfDay } from "date-fns";
import { useEffect, useRef, useState } from "react";
import {
  ALL_PRESETS,
  blurActiveElement,
  customRangeLabel,
  FILTER_FIELD_H,
  FILTER_FIELD_W,
  PAST_PRESETS,
  presetToRange,
  type RangeValue,
} from "./DateRangeSelect";
import type { FilterOption } from "./FilterSelect";

/**
 * Paired filter cells: one 220x40 select-styled field opening a two-column
 * popover, so related filters (Source/Target type, Last/Next run) share a
 * single cell instead of crowding the filter row. Columns apply on click and
 * the popover stays open until a click-away (fully deselecting the field).
 */

function PairFieldShell({
  label,
  summary,
  hasValue,
  children,
  popoverWidth,
}: {
  label: string;
  summary: string;
  hasValue: boolean;
  children: (close: () => void) => React.ReactNode;
  popoverWidth: number;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const labelId = `pair-${label.replace(/\s+/g, "-").toLowerCase()}`;

  const close = () => {
    setOpen(false);
    blurActiveElement();
  };

  return (
    <FormControl size="small" sx={{ width: FILTER_FIELD_W }} ref={anchorRef}>
      <InputLabel id={labelId} shrink={hasValue}>
        {label}
      </InputLabel>
      {/* Select is display-only: the popover below is the real menu. */}
      <Select
        labelId={labelId}
        label={label}
        notched={hasValue}
        value=""
        displayEmpty
        renderValue={() => summary}
        open={false}
        onOpen={() => setOpen(true)}
        sx={{ height: FILTER_FIELD_H }}
      >
        <MenuItem value="" sx={{ display: "none" }} />
      </Select>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          elevation: 0,
          // STRONG_DROPDOWN_PAPER_SX is a theme FUNCTION — compose via an
          // sx array (spreading a function yields {} and loses the shadow).
          sx: [STRONG_DROPDOWN_PAPER_SX, { width: popoverWidth }],
        }}
      >
        {children(close)}
      </Popover>
    </FormControl>
  );
}

function ColumnHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing={0.5}
      sx={{ px: 2, pt: 1.5, display: "block" }}
    >
      {children}
    </Typography>
  );
}

/** Source/Target connection-type pair in one cell ("SFTP → S3"). */
export function TypePairFilter({
  options,
  source,
  target,
  onChange,
}: {
  options: FilterOption[];
  source: string | undefined;
  target: string | undefined;
  onChange: (next: { source?: string; target?: string }) => void;
}) {
  const labelOf = (value: string | undefined) =>
    value ? (options.find((o) => o.value === value)?.label ?? value) : "Any";
  const hasValue = Boolean(source || target);
  const summary = hasValue ? `${labelOf(source)} → ${labelOf(target)}` : "";

  const column = (
    title: string,
    selected: string | undefined,
    apply: (value?: string) => void,
  ) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <ColumnHeader>{title}</ColumnHeader>
      <MenuList dense>
        <MenuItem selected={!selected} onClick={() => apply(undefined)}>
          Any
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={selected === option.value}
            onClick={() => apply(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Box>
  );

  return (
    <PairFieldShell
      label="Source / Target"
      summary={summary}
      hasValue={hasValue}
      popoverWidth={340}
    >
      {() => (
        <Box sx={{ display: "flex" }}>
          {column("Source type", source, (value) =>
            onChange({ source: value, target }),
          )}
          <Divider orientation="vertical" flexItem />
          {column("Target type", target, (value) =>
            onChange({ source, target: value }),
          )}
        </Box>
      )}
    </PairFieldShell>
  );
}

const CUSTOM = "__custom__";

/** Last/Next run date-range pair in one cell. */
export function RunDatePairFilter({
  lastRun,
  nextRun,
  onChange,
}: {
  lastRun: RangeValue;
  nextRun: RangeValue;
  onChange: (next: { lastRun: RangeValue; nextRun: RangeValue }) => void;
}) {
  const [keys, setKeys] = useState<{ last: string; next: string }>({
    last: "",
    next: "",
  });
  const [custom, setCustom] = useState<null | {
    column: "last" | "next";
    start: Date | null;
    end: Date | null;
  }>(null);

  const hasLast = Boolean(lastRun.after || lastRun.before);
  const hasNext = Boolean(nextRun.after || nextRun.before);
  const hasValue = hasLast || hasNext;

  // Clear Filters resets the values from outside - drop the preset keys too.
  useEffect(() => {
    setKeys((prev) => ({
      last: hasLast ? prev.last : "",
      next: hasNext ? prev.next : "",
    }));
  }, [hasLast, hasNext]);

  const summaryPart = (key: string, range: RangeValue) => {
    if (key === CUSTOM) return customRangeLabel(range.after, range.before);
    return ALL_PRESETS.find((p) => p.value === key)?.label ?? "Any";
  };
  const summary = hasValue
    ? `Last: ${summaryPart(keys.last, lastRun)} · Next: ${summaryPart(keys.next, nextRun)}`
    : "";

  const apply = (column: "last" | "next", key: string, range: RangeValue) => {
    setKeys((prev) => ({ ...prev, [column]: key }));
    onChange({
      lastRun: column === "last" ? range : lastRun,
      nextRun: column === "next" ? range : nextRun,
    });
  };

  const column = (
    title: string,
    columnKey: "last" | "next",
    presets: readonly { value: string; label: string }[],
  ) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <ColumnHeader>{title}</ColumnHeader>
      <MenuList dense>
        <MenuItem
          selected={!keys[columnKey]}
          onClick={() => apply(columnKey, "", {})}
        >
          Anytime
        </MenuItem>
        {presets.map((preset) => (
          <MenuItem
            key={preset.value}
            selected={keys[columnKey] === preset.value}
            onClick={() =>
              apply(columnKey, preset.value, presetToRange(preset.value))
            }
          >
            {preset.label}
          </MenuItem>
        ))}
        <MenuItem
          selected={keys[columnKey] === CUSTOM}
          onClick={() => {
            const range = columnKey === "last" ? lastRun : nextRun;
            setCustom({
              column: columnKey,
              start: range.after ? new Date(range.after) : null,
              end: range.before ? new Date(range.before) : null,
            });
          }}
        >
          Select Period
        </MenuItem>
      </MenuList>
    </Box>
  );

  return (
    <PairFieldShell
      label="Last / Next Run"
      summary={summary}
      hasValue={hasValue}
      popoverWidth={360}
    >
      {() =>
        custom ? (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {custom.column === "last" ? "Last run" : "Next run"} period
              </Typography>
              <DatePicker
                label="From"
                value={custom.start}
                onChange={(d) => setCustom((c) => c && { ...c, start: d })}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={custom.end}
                onChange={(d) => setCustom((c) => c && { ...c, end: d })}
                minDate={custom.start ?? undefined}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
              >
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setCustom(null)}
                >
                  Back
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    apply(custom.column, CUSTOM, {
                      after: custom.start
                        ? startOfDay(custom.start).toISOString()
                        : undefined,
                      before: custom.end
                        ? endOfDay(custom.end).toISOString()
                        : undefined,
                    });
                    setCustom(null);
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        ) : (
          <Box sx={{ display: "flex" }}>
            {column("Last run", "last", PAST_PRESETS)}
            <Divider orientation="vertical" flexItem />
            {column("Next run", "next", ALL_PRESETS)}
          </Box>
        )
      }
    </PairFieldShell>
  );
}
